import React, { useState, useEffect, useRef } from "react";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Inline icons (24x24 lucide outlines) — replaces the lucide-react dependency.
const iconProps = (size) => ({
  xmlns: "http://www.w3.org/2000/svg",
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
});
const Search = ({ size = 24 }) => (
  <svg {...iconProps(size)}><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
);
const MenuIcon = ({ size = 24 }) => (
  <svg {...iconProps(size)}><path d="M4 6h16" /><path d="M4 12h16" /><path d="M4 18h16" /></svg>
);
const X = ({ size = 24 }) => (
  <svg {...iconProps(size)}><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
);

const navData = [
  {
    title: "Services",
    items: [
      {
        title: "B2B Marketing Strategy",
        href: "/b2b-marketing-strategy/",
        src: "/.netlify/images?url=/assets/images/pallant-consultants-reviewing-marketing-on-tablet.jpg&w=400",
        description: "Making you easier to choose and harder to compete with."
      },
      {
        title: "B2B Websites",
        href: "/websites/",
        src: "/.netlify/images?url=/assets/images/pallant-consultants-discussing-strategy-at-laptop.jpg&w=400",
        description: "Get a new website every 24 months."
      },
      {
        title: "Google Ads",
        href: "/google-ads/",
        src: "/.netlify/images?url=/assets/images/pallant-team-strategy-session-at-table.jpg&w=400",
        description: "Show up for what you do where you do it."
      },
      {
        title: "LinkedIn Ads",
        href: "/linkedin-ads/",
        src: "/.netlify/images?url=/assets/images/pallant-consultants-working-at-window-desk.jpg&w=400",
        description: "Break through the network barrier"
      },
      {
        title: "SEO, AEO & AI Search",
        href: "/seo-ai-search/",
        src: "/.netlify/images?url=/assets/images/pallant-team-collaborating-around-table.jpg&w=400",
        description: "Rank higher to capture extreme high-intent leads."
      },
      {
        title: "Photography",
        href: "/photography/",
        src: "/.netlify/images?url=/assets/images/Photography%20page/Capability/velocetec-technician-holding-precision-part.jpg&w=400",
        description: "Proof buyers can see"
      },
      {
        title: "Case Studies & Content",
        href: "/case-studies-and-content/",
        src: "/.netlify/images?url=/assets/images/pallant-team-reviewing-engineered-components-with-client.jpg&w=400",
        description: "Turn your best projects into proof that wins the next one."
      },
      {
        title: "Fully Managed Marketing",
        href: "/fully-managed-marketing/",
        src: "/.netlify/images?url=/assets/images/pallant-team-meeting-in-studio-workspace.jpg&w=400",
        description: "Website, content and ads, done for you, from £399 per month."
      }
    ],
    // A full-width banner under the card grid. Only Services has one; the
    // mobile drawer renders it as the final row of the same section.
    featured: {
      title: "The Shortlist Test",
      href: "/would-you-make-the-shortlist/",
      description: "A free recorded walkthrough of how a buyer sees your website. It takes four minutes to request, and you hear back from Ben or Simon within three working days."
    }
  },
  {
    // Sectors sits next to Services: the two answer the same kind of question,
    // what we do and who we do it for. Items carry src/description so it renders
    // as the same image-and-text mega menu, with room for the sector hubs on the
    // roadmap. The MENU structure is not the URL structure — sector pages stay at
    // /marketing-for-<sector>-companies/ at the root and are not re-nested.
    title: "Sectors",
    items: [
      {
        title: "Manufacturing",
        href: "/marketing-for-manufacturing-companies/",
        src: "/.netlify/images?url=/assets/images/Pallant%20Studio%20Sectors%20Images/sector-tool-making-and-manufacturing-landscape.jpg&w=400",
        description: "Be easier to choose than the facility down the road."
      },
      {
        title: "Engineering",
        href: "/marketing-for-engineering-companies/",
        src: "/.netlify/images?url=/assets/images/Pallant%20Studio%20Sectors%20Images/sector-engineering-landscape.jpg&w=400",
        description: "Turn technical expertise into new clients."
      },
      {
        title: "Defence Supply Chain",
        href: "/marketing-for-defence-supply-chain/",
        src: "/.netlify/images?url=/assets/images/Pallant%20Studio%20Sectors%20Images/sector-defence-supply-chain.jpg&w=400",
        description: "Position your capability as defence-ready."
      }
    ]
  },
  {
    title: "Our Work",
    href: "/our-work/",
    items: []
  },
  {
    // Grouped rather than one flat list: guides, the UKMIS cluster and the
    // article feed are three different things. `groups` is generic — Sectors can
    // use it too once it outgrows a single column.
    title: "Insights",
    href: "/insights/",
    groups: [
      {
        heading: "Guides",
        items: [
          { title: "Scaling beyond your network", href: "/scaling-beyond-your-network/" },
          { title: "Expanding into new sectors", href: "/expanding-into-new-sectors/" },
          { title: "Value before a sale or exit", href: "/increase-the-value-of-your-business-for-sale-or-exit/" },
          { title: "B2B Marketing Process", href: "/b2b-marketing-process/" },
          { title: "B2B Marketing Strategy", href: "/b2b-marketing-strategy/" }
        ]
      },
      {
        heading: "UK Modern Industrial Strategy",
        items: [
          { title: "The analysis", href: "/uk-modern-industrial-strategy/" },
          { title: "Advanced Manufacturing", href: "/uk-modern-industrial-strategy/advanced-manufacturing-in-the-uk-modern-industrial-strategy/" },
          { title: "Defence", href: "/uk-modern-industrial-strategy/defence-industry-opportunities-in-the-uk-modern-industrial-strategy/" },
          { title: "Clean Energy", href: "/uk-modern-industrial-strategy/clean-energy-industries-in-the-uk-modern-industrial-strategy/" },
          { title: "Digital and Technologies", href: "/uk-modern-industrial-strategy/digital-and-technologies-in-the-uk-modern-industrial-strategy/" },
          { title: "Life Sciences", href: "/uk-modern-industrial-strategy/life-sciences-in-the-uk-modern-industrial-strategy/" },
          { title: "Quarterly updates", href: "/uk-modern-industrial-strategy/quarterly-updates/" }
        ]
      },
      {
        // The three most read articles, then the feed. Ranked on Plausible
        // visitors over the six months to 25 August 2026, combining each post's
        // /insights/ URL with the top-level WordPress URL that still redirects
        // to it, since the traffic is split across both: 62, 30 and 14 visitors
        // against 13 for the next one. Titles are trimmed to fit the column, in
        // the same way the UKMIS labels are. Worth re-checking when the feed
        // grows or a post takes off.
        heading: "Articles",
        items: [
          { title: "How to find new clients", href: "/insights/how-to-find-new-clients-for-manufacturing-and-engineering-companies/" },
          { title: "Marketing when you have an NDA", href: "/insights/how-to-do-marketing-when-you-have-an-nda/" },
          { title: "How to differentiate your business", href: "/insights/how-to-differentiate-your-engineering-and-manufacturing-business/" },
          { title: "All insights", href: "/insights/" }
        ]
      }
    ]
  },
  {
    title: "About",
    href: "/about/",
    items: []
  }
];

const MenuItem = ({ setActive, active, item, children, align = "center", href }) => {
  // alignment maps
  const alignClass = align === "left" 
    ? "left-0 transform -translate-x-4" 
    : align === "right" 
    ? "right-0 transform translate-x-4" 
    : "left-1/2 transform -translate-x-1/2";
    
  const content = (
      <p className="cursor-pointer text-brand-navy hover:text-brand-blue transition-colors font-heading font-semibold text-[17px]">
        {item}
      </p>
  );

  return (
    <div onMouseEnter={() => setActive(item)} className="relative px-2">
      {href ? <a href={href} className="block">{content}</a> : content}
      {active !== null && active === item && children && (
        <div className={cn("absolute top-full pt-4 z-50 pointer-events-auto", alignClass)}>
          <div className="nav-drop-enter origin-top bg-white/95 backdrop-blur-2xl rounded-2xl overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.15)] border border-brand-lightGrey/30">
            <div className="w-max h-full p-4">
              {children}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Menu = ({ setActive, children, solid }) => {
  return (
    <nav
      onMouseLeave={() => setActive(null)}
      className={cn(
        "relative rounded-full flex justify-center space-x-6 px-10 items-center pointer-events-auto h-[54px] transition-all duration-300",
        solid
          ? "border border-transparent" // solid bar behind it — no pill chrome needed
          : "border border-brand-teal/10 bg-white/70 backdrop-blur-md shadow-sm"
      )}
    >
      {children}
    </nav>
  );
};

const ProductItem = ({ title, description, href, src, cta = false }) => {
  return (
    <a
      href={href}
      className={cn(
        "flex space-x-6 p-4 rounded-xl transition-colors items-center group cursor-pointer",
        cta
          ? "bg-brand-aqua/15 border border-brand-aqua/40 hover:bg-brand-aqua/25"
          : "hover:bg-brand-lightGrey/20"
      )}
    >
      <div className="w-32 h-20 rounded-md overflow-hidden shadow-sm flex-shrink-0">
        <img
            src={src}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div>
        <h4 className={cn(
          "text-[17px] font-heading font-semibold text-brand-navy mb-1 transition-colors",
          cta ? "group-hover:text-brand-teal" : "group-hover:text-brand-blue"
        )}>
          {title}
        </h4>
        <p className="text-brand-text font-body text-[15px] max-w-[14rem] leading-snug">{description}</p>
      </div>
    </a>
  );
};

const HoveredLink = ({ children, ...rest }) => {
  return (
    <a
      {...rest}
      className="text-brand-text hover:text-brand-blue font-body transition-colors text-[17px] hover:bg-brand-lightGrey/20 block px-4 py-2 rounded-lg cursor-pointer"
    >
      {children}
    </a>
  );
};

export default function Navbar() {
  const [active, setActive] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isDarkBg, setIsDarkBg] = useState(true); // Hero matches true initially
  const [scrolled, setScrolled] = useState(false); // Solid bar once past the hero
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const logoRef = useRef(null);
  const searchInputRef = useRef(null);

  // The search field is always mounted (its width animates in CSS), so focus
  // it imperatively when opened — the autoFocus attribute would grab focus on load.
  useEffect(() => {
    if (searchOpen && searchInputRef.current) searchInputRef.current.focus();
  }, [searchOpen]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/search/?q=${encodeURIComponent(searchQuery.trim())}`;
    }
  };

  // Scroll tracker logic perfectly tracking logo Y offset dynamically mapping underlying semantic section boxes natively matching color payloads!
  useEffect(() => {
    const handleScroll = () => {
      // Switch to the solid bar once the hero header has scrolled past the nav
      const hero = document.querySelector('header');
      setScrolled(hero ? hero.getBoundingClientRect().bottom <= 80 : window.scrollY > 200);

      if (!logoRef.current) return;
      const rect = logoRef.current.getBoundingClientRect();
      const logoCenterY = rect.top + rect.height / 2;
      const logoCenterX = rect.left + rect.width / 2;
      
      // elementFromPoint returns Z-index ordered array (topmost element first)
      const elementsUnder = document.elementsFromPoint(logoCenterX, logoCenterY);
      
      const darkClasses = ['bg-brand-navy', 'bg-brand-teal', 'bg-brand-blue', 'bg-[#3758FF]', 'bg-[#123840]', 'bg-[#1a2342]'];
      const lightClasses = ['bg-brand-offWhite', 'bg-brand-aqua', 'bg-white', 'bg-[#6DD5CE]', 'section-who-works-with-us', 'bg-brand-lightGrey'];
      
      let detectedDark = null;
      
      for (const el of elementsUnder) {
        if (!el.className || typeof el.className !== 'string') continue;
        
        const hasDark = darkClasses.some(c => el.className.includes(c));
        const hasLight = lightClasses.some(c => el.className.includes(c));
        
        if (hasDark) {
          detectedDark = true;
          break;
        }
        if (hasLight) {
          detectedDark = false;
          break;
        }
      }
      
      if (detectedDark !== null) {
        setIsDarkBg(detectedDark);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    // Trigger measurement reliably directly right after bootup rendering logic!
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on resize to desktop properly gracefully maintaining view ports efficiently!
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className={cn(
      // The solid white bar behind the nav is desktop-only (md:) — on mobile the
      // pill itself goes solid instead (see the mobile bar below), same trigger.
      "fixed inset-x-0 z-[100] transition-all duration-300 motion-reduce:transition-none",
      scrolled
        ? "top-0 py-3 md:bg-white md:shadow-[0_2px_20px_rgba(12,19,49,0.08)] pointer-events-auto"
        : "top-6 pointer-events-none"
    )}>
     <div className="w-full max-w-[90rem] mx-auto px-6 xl:px-12">

      {/* Desktop Navigation */}
      <div className="hidden md:flex items-center justify-between gap-6">
        
        {/* Brand */}
        <div className="flex-1 flex pointer-events-auto h-[54px] items-center" ref={logoRef}>
            <a href="/" className="cursor-pointer">
                <img
                    src={!scrolled && isDarkBg ? "/assets/images/pallant-primary-logo-platinum.svg" : "/assets/images/pallant-primary-logo-oxford-blue.svg"}
                    alt="Pallant Studio"
                    className={cn("h-[54px] w-auto transition-all duration-300 pointer-events-none", !scrolled && "drop-shadow-md")}
                    width="135"
                    height="54"
                />
            </a>
        </div>

        {/* Floating Menu Pills */}
        <div className="flex-shrink-0">
          <Menu setActive={setActive} solid={scrolled}>
            {navData.map((navSection, idx) => (
              <MenuItem key={idx} setActive={setActive} active={active} item={navSection.title} align="center" href={navSection.href}>
                {navSection.groups ? (
                  /* Grouped mega menu: a column per heading. */
                  <div className="flex gap-6 py-2">
                    {navSection.groups.map((group, gIdx) => (
                      <div key={gIdx} className="min-w-[210px]">
                        <p className="font-heading font-bold text-brand-navy text-[14px] uppercase tracking-wider px-4 pb-2">{group.heading}</p>
                        <div className="flex flex-col space-y-1">
                          {group.items.map((subItem, sIdx) => (
                            <HoveredLink key={sIdx} href={subItem.href}>{subItem.title}</HoveredLink>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : navSection.items && navSection.items.length > 0 && (
                  /* Items carrying an image render as the picture mega menu
                     (Services, Sectors); everything else is a plain list. */
                  navSection.items.some(i => i.src) ? (
                    <div className="flex flex-col gap-4">
                      <div className="grid grid-cols-2 gap-4">
                        {navSection.items.map((subItem, sIdx) => (
                          <ProductItem
                            key={sIdx}
                            title={subItem.title}
                            href={subItem.href}
                            src={subItem.src}
                            description={subItem.description}
                            cta={subItem.cta}
                          />
                        ))}
                      </div>
                      {navSection.featured && (
                        <a
                          href={navSection.featured.href}
                          className="block rounded-xl bg-brand-aqua px-6 py-5 text-brand-navy transition-colors hover:bg-brand-aqua/80 cursor-pointer"
                        >
                          <span className="block font-heading font-semibold text-[17px] mb-1">{navSection.featured.title}</span>
                          <span className="block font-body text-[15px] leading-snug">{navSection.featured.description}</span>
                        </a>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col space-y-1 py-2 min-w-[170px]">
                      {navSection.items.map((subItem, sIdx) => (
                        <HoveredLink key={sIdx} href={subItem.href}>{subItem.title}</HoveredLink>
                      ))}
                    </div>
                  )
                )}
              </MenuItem>
            ))}
          </Menu>
        </div>

        <div className="flex-1 flex justify-end gap-3 pointer-events-auto h-[54px] items-center">
          
          {/* Search Toggle */}
          <div className="relative flex items-center">
            <form
              onSubmit={handleSearchSubmit}
              className={cn(
                "overflow-hidden transition-all duration-300 motion-reduce:transition-none",
                searchOpen ? "w-[220px] opacity-100 mr-2" : "w-0 opacity-0 mr-0 invisible"
              )}
            >
                <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                ref={searchInputRef}
                tabIndex={searchOpen ? 0 : -1}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-[54px] bg-white/70 backdrop-blur-md border border-brand-teal/10 rounded-full px-6 font-heading font-semibold text-[17px] text-brand-navy outline-none focus:border-brand-aqua shadow-sm"
              />
            </form>
            <button 
              onClick={() => setSearchOpen(!searchOpen)} 
              className="w-[54px] h-[54px] shrink-0 rounded-full flex items-center justify-center transition-colors shadow-sm cursor-pointer text-brand-navy bg-white/70 backdrop-blur-md border border-brand-teal/10 hover:bg-white/90"
              aria-label="Search"
            >
              <Search size={20} />
            </button>
          </div>

          <a href="/contact/" className="bg-brand-aqua text-brand-navy hover:scale-[1.03] px-7 h-full flex items-center justify-center rounded-full font-heading font-bold text-[17px] transition-transform shadow-lg shadow-brand-aqua/20 border-2 border-brand-aqua shrink-0 cursor-pointer">
            Let's Talk
          </a>
        </div>
      </div>

      {/* Mobile Navigation Bar — translucent pill at the top of the page, fading
          to solid white once scrolled (in place of the desktop bar behind it) */}
      <div className={cn(
        "md:hidden flex items-center justify-between rounded-full px-5 py-3 border border-brand-teal/10 shadow-lg pointer-events-auto transition-colors duration-300 motion-reduce:transition-none",
        scrolled ? "bg-white" : "bg-white/90 backdrop-blur-md"
      )}>
        <a href="/" className="cursor-pointer">
            <img src="/assets/images/pallant-primary-logo-oxford-blue.svg" alt="Pallant Studio" className="h-9 w-auto" width="90" height="36" />
        </a>
        <div className="flex items-center gap-3">
            <a 
              href="/search/"
              className="text-brand-navy focus:outline-none w-10 h-10 flex items-center justify-center bg-white border border-brand-lightGrey/50 rounded-full cursor-pointer"
              aria-label="Search"
            >
              <Search size={20} />
            </a>
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-brand-navy focus:outline-none cursor-pointer"
              aria-label="Toggle navigation menu"
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X size={28} /> : <MenuIcon size={28} />}
            </button>
        </div>
      </div>

      {/* Mobile Dropdown Panel Container */}
      <div className="relative pointer-events-auto w-full flex justify-end">
            {mobileMenuOpen && (
            <div
                className="nav-drop-enter absolute top-4 left-0 right-0 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-brand-lightGrey/30 overflow-hidden origin-top"
            >
                <div className="flex flex-col p-6 max-h-[75vh] overflow-y-auto">
                
                {searchOpen && (
                  <form onSubmit={handleSearchSubmit} className="mb-6 flex shadow-sm">
                    <input 
                      type="text" 
                      placeholder="Search..." 
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="flex-1 bg-white border border-brand-lightGrey/50 rounded-l-full px-6 py-3 font-heading font-semibold text-[17px] text-brand-navy outline-none focus:border-brand-aqua"
                    />
                    <button type="submit" className="bg-brand-aqua text-brand-navy px-5 rounded-r-full font-heading font-bold border border-brand-aqua">
                      Search
                    </button>
                  </form>
                )}
                
                {navData.map((navSection, idx) => (
                  <div key={idx} className="mb-6">
                      <h3 className="font-heading font-semibold text-brand-navy text-xl mb-3">
                        {navSection.href ? (
                          <a href={navSection.href} className="hover:text-brand-aqua transition-colors cursor-pointer">{navSection.title}</a>
                        ) : (
                          navSection.title
                        )}
                      </h3>
                      {navSection.groups ? (
                        <div className="space-y-4">
                          {navSection.groups.map((group, gIdx) => (
                            <div key={gIdx}>
                              <p className="font-heading font-bold text-brand-navy text-[14px] uppercase tracking-wider mb-1 pl-2">{group.heading}</p>
                              <div className="flex flex-col space-y-1 pl-2 border-l-2 border-brand-lightGrey/40">
                                {group.items.map((subItem, sIdx) => (
                                  <HoveredLink key={sIdx} href={subItem.href}>{subItem.title}</HoveredLink>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : navSection.items && navSection.items.length > 0 && (
                        <div className="flex flex-col space-y-1 pl-2 border-l-2 border-brand-lightGrey/40">
                          {navSection.items.map((subItem, sIdx) => (
                            <HoveredLink key={sIdx} href={subItem.href}>{subItem.title}</HoveredLink>
                          ))}
                          {navSection.featured && (
                            <a
                              href={navSection.featured.href}
                              className="block rounded-xl bg-brand-aqua px-4 py-3 text-brand-navy transition-colors hover:bg-brand-aqua/80 cursor-pointer"
                            >
                              <span className="block font-heading font-semibold text-[17px]">{navSection.featured.title}</span>
                              <span className="block font-body text-[15px] leading-snug">{navSection.featured.description}</span>
                            </a>
                          )}
                        </div>
                      )}
                  </div>
                ))}

                <div className="pt-6 border-t border-brand-lightGrey/40 mt-auto">
                    <a href="/contact/" onClick={() => setMobileMenuOpen(false)} className="flex justify-center items-center w-full bg-brand-aqua text-brand-navy py-4 rounded-full font-heading font-bold text-lg shadow-md shadow-brand-aqua/20 cursor-pointer">
                    Let's Talk
                    </a>
                </div>

                </div>
            </div>
            )}
      </div>

     </div>
    </div>
  );
}
